import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Check,
  X,
  Star,
  Zap,
  Shield,
  ArrowRight,
  Sparkles,
  MessageSquare,
  BarChart3,
  Bell,
  Users,
  Globe,
  HeadphonesIcon,
  QrCode,
  Mail,
  Smartphone,
  FileText,
  TrendingUp,
  Clock,
  Award,
  Target,
  Brain,
  Lock
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const oneTimeReport = {
  name: "Snapshot Report",
  price: 99,
  description: "One-time comprehensive business analysis",
  features: [
    "Complete 8-Section Analysis",
    "Google-Verified Business Data",
    "AI-Powered Recommendations",
    "Competitor Discovery (5 businesses)",
    "Grade A/O/C Assessment Views",
    "Budget & ROI Projections",
    "Downloadable PDF Report",
    "Free Resource Guide (40+ tools)"
  ],
  cta: "Get Your Report",
  icon: FileText
};

const plans = [
  {
    name: "Starter",
    description: "Essential reputation management",
    monthlyPrice: 149,
    annualPrice: 127,
    popular: false,
    features: [
      { text: "1 Business Location", included: true },
      { text: "Google, Yelp, Facebook Monitoring", included: true },
      { text: "Real-Time Review Alerts", included: true },
      { text: "SMS Review Invites (100/mo)", included: true },
      { text: "Email Review Campaigns (500/mo)", included: true },
      { text: "QR Code Review Generator", included: true },
      { text: "Basic Analytics Dashboard", included: true },
      { text: "Monthly Snapshot Reports", included: true },
      { text: "Email Support (24hr response)", included: true },
      { text: "AI Review Response Suggestions", included: false },
      { text: "Competitor Tracking", included: false },
      { text: "Reputation Suppression Tools", included: false },
      { text: "White-Label Reports", included: false },
      { text: "API Access", included: false },
    ],
    cta: "Get Started",
    color: "slate",
    bestFor: "Solo businesses & startups"
  },
  {
    name: "Professional",
    description: "Advanced tools for growth",
    monthlyPrice: 249,
    annualPrice: 212,
    popular: true,
    features: [
      { text: "Up to 5 Business Locations", included: true },
      { text: "All 50+ Review Platforms", included: true },
      { text: "Instant Review Alerts (SMS + Email)", included: true },
      { text: "SMS Review Invites (500/mo)", included: true },
      { text: "Email Review Campaigns (2,500/mo)", included: true },
      { text: "Custom Branded QR Codes", included: true },
      { text: "Advanced Analytics & Trends", included: true },
      { text: "Weekly Snapshot Reports", included: true },
      { text: "Priority Support (4hr response)", included: true },
      { text: "AI Review Responses (Unlimited)", included: true },
      { text: "Competitor Tracking (10 competitors)", included: true },
      { text: "Sentiment Analysis & Insights", included: true },
      { text: "Google Business Profile Sync", included: true },
      { text: "White-Label Reports", included: false },
    ],
    cta: "Get Started",
    color: "blue",
    bestFor: "Growing businesses & agencies"
  },
  {
    name: "Enterprise",
    description: "Complete reputation command center",
    monthlyPrice: 449,
    annualPrice: 382,
    popular: false,
    features: [
      { text: "Unlimited Business Locations", included: true },
      { text: "All 50+ Review Platforms", included: true },
      { text: "Instant Alerts + Escalation Rules", included: true },
      { text: "SMS Review Invites (Unlimited)", included: true },
      { text: "Email Campaigns (Unlimited)", included: true },
      { text: "Fully Branded Review Funnels", included: true },
      { text: "Custom Reports & Dashboards", included: true },
      { text: "Daily Automated Reports", included: true },
      { text: "Dedicated Account Manager", included: true },
      { text: "AI Auto-Response + Approval Flows", included: true },
      { text: "Unlimited Competitor Tracking", included: true },
      { text: "Advanced Sentiment + NLP Analysis", included: true },
      { text: "Full Platform Integrations", included: true },
      { text: "Reputation Suppression Suite", included: true },
      { text: "White-Label Everything", included: true },
      { text: "API Access + Webhooks", included: true },
    ],
    cta: "Contact Sales",
    color: "emerald",
    bestFor: "Agencies & multi-location brands"
  }
];

const whyUs = [
  {
    icon: Brain,
    title: "AI That Actually Works",
    description: "Our AI doesn't just suggest responses—it learns your brand voice and crafts replies that convert critics into advocates."
  },
  {
    icon: Zap,
    title: "Real-Time, Not Delayed",
    description: "Most platforms check reviews hourly. We monitor continuously with instant alerts so you never miss a moment."
  },
  {
    icon: Shield,
    title: "Suppression That Protects",
    description: "Identify and neutralize reputation threats with our proprietary suppression tools and strategic response protocols."
  },
  {
    icon: Target,
    title: "ROI You Can Measure",
    description: "Every dollar you spend shows 2-3x return. We track leads, conversions, and revenue impact—not just vanity metrics."
  },
  {
    icon: Lock,
    title: "Enterprise Security",
    description: "SOC 2 compliant, encrypted end-to-end, with role-based access. Your data is yours, protected always."
  },
  {
    icon: Award,
    title: "Concierge Onboarding",
    description: "We don't just give you software—we set everything up, train your team, and ensure you're seeing results in week one."
  }
];

export default function PricingPage() {
  const [annual, setAnnual] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-28 pb-20 px-4">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <Badge className="bg-blue-100 text-blue-700 mb-4">
            Trusted by 500+ Businesses
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Invest in Your
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 block">
              Reputation
            </span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Your reputation is your most valuable asset. Protect it with the most comprehensive 
            reputation management platform available.
          </p>
        </div>

        {/* One-Time Report */}
        <div className="max-w-4xl mx-auto mb-16">
          <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50 overflow-hidden" data-testid="report-pricing">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                      <FileText className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-slate-900">{oneTimeReport.name}</h3>
                      <p className="text-slate-600">{oneTimeReport.description}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="text-5xl font-bold text-slate-900">${oneTimeReport.price}</span>
                    <span className="text-slate-500">one-time</span>
                  </div>
                  
                  <Link to="/lookup">
                    <Button size="lg" className="bg-blue-600 hover:bg-blue-700 w-full md:w-auto">
                      {oneTimeReport.cta}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </div>
                
                <div>
                  <ul className="grid grid-cols-1 gap-2">
                    {oneTimeReport.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-slate-700">
                        <Check className="h-5 w-5 text-blue-600 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Plans Header */}
        <div className="max-w-4xl mx-auto text-center mb-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Monthly Management Plans
          </h2>
          <p className="text-slate-600 mb-6">
            Ongoing protection and growth for your reputation
          </p>
          
          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4">
            <span className={`text-sm font-medium ${!annual ? 'text-slate-900' : 'text-slate-500'}`}>Monthly</span>
            <Switch
              checked={annual}
              onCheckedChange={setAnnual}
              data-testid="billing-toggle"
            />
            <span className={`text-sm font-medium ${annual ? 'text-slate-900' : 'text-slate-500'}`}>
              Annual
              <Badge className="ml-2 bg-emerald-100 text-emerald-700">Save 15%</Badge>
            </span>
          </div>
        </div>
        
        {/* Pricing Cards */}
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8 mb-20">
          {plans.map((plan) => (
            <Card 
              key={plan.name}
              className={`relative border-2 ${
                plan.popular 
                  ? 'border-blue-500 shadow-2xl scale-105 z-10' 
                  : 'border-slate-200'
              }`}
              data-testid={`plan-${plan.name.toLowerCase()}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <Badge className="bg-blue-600 text-white px-4 py-1 shadow-lg">
                    <Star className="h-3 w-3 mr-1 fill-white" />
                    Most Popular
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-2 pt-8">
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
                <p className="text-xs text-blue-600 font-medium mt-1">Best for: {plan.bestFor}</p>
              </CardHeader>
              
              <CardContent className="pt-4">
                {/* Price */}
                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-slate-900">
                      ${annual ? plan.annualPrice : plan.monthlyPrice}
                    </span>
                    <span className="text-slate-500">/month</span>
                  </div>
                  {annual && (
                    <p className="text-sm text-emerald-600 mt-1">
                      Save ${(plan.monthlyPrice - plan.annualPrice) * 12}/year
                    </p>
                  )}
                </div>
                
                {/* Features */}
                <ul className="space-y-2.5 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      {feature.included ? (
                        <Check className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="h-5 w-5 text-slate-300 flex-shrink-0 mt-0.5" />
                      )}
                      <span className={`text-sm ${feature.included ? 'text-slate-700' : 'text-slate-400'}`}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>
                
                {/* CTA */}
                <Button 
                  className={`w-full ${
                    plan.popular 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-slate-900 hover:bg-slate-800'
                  }`}
                  size="lg"
                  data-testid={`cta-${plan.name.toLowerCase()}`}
                >
                  {plan.cta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Why Selletrust */}
        <div className="max-w-6xl mx-auto mb-20">
          <div className="text-center mb-12">
            <Badge className="bg-blue-100 text-blue-700 mb-4">Why Selletrust</Badge>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              The Best Reputation Management. Period.
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We're not just another review tool. We're your complete reputation command center.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyUs.map((item, idx) => (
              <Card key={idx} className="border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        
        {/* Platforms */}
        <div className="max-w-4xl mx-auto mb-20">
          <Card className="border-slate-200 bg-slate-50">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  50+ Platforms. One Dashboard.
                </h3>
                <p className="text-slate-600">
                  We monitor everywhere your customers are talking.
                </p>
              </div>
              
              <div className="flex flex-wrap justify-center gap-3">
                {[
                  "Google", "Yelp", "Facebook", "BBB", "Trustpilot", "TripAdvisor",
                  "Healthgrades", "Avvo", "Zillow", "Angi", "HomeAdvisor", "G2",
                  "Capterra", "Indeed", "Glassdoor", "Apple Maps", "Bing", "Yahoo"
                ].map((platform) => (
                  <Badge 
                    key={platform} 
                    variant="outline" 
                    className="px-4 py-2 text-sm bg-white"
                  >
                    {platform}
                  </Badge>
                ))}
                <Badge className="px-4 py-2 text-sm bg-blue-600 text-white">
                  +32 more
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Guarantee */}
        <div className="max-w-4xl mx-auto mb-20">
          <Card className="border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
            <CardContent className="p-8 text-center">
              <Shield className="h-16 w-16 text-emerald-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Our Commitment to You
              </h3>
              <p className="text-slate-600 max-w-2xl mx-auto mb-6">
                We're so confident in our platform that we guarantee results. If you don't see 
                measurable improvement in your online reputation within 90 days, we'll work with 
                you one-on-one until you do—at no additional cost.
              </p>
              <div className="flex justify-center gap-8 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-emerald-600" />
                  90-Day Results Guarantee
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-emerald-600" />
                  No Long-Term Contracts
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-emerald-600" />
                  Cancel Anytime
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* CTA Section */}
        <div className="max-w-4xl mx-auto">
          <Card className="border-0 bg-slate-900 text-white overflow-hidden">
            <CardContent className="p-12 text-center relative">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600 rounded-full -translate-y-1/2 translate-x-1/2 opacity-20"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-500 rounded-full translate-y-1/2 -translate-x-1/2 opacity-20"></div>
              
              <div className="relative z-10">
                <Sparkles className="h-12 w-12 mx-auto mb-6 text-blue-400" />
                <h3 className="text-3xl font-bold mb-4">
                  Ready to Take Control?
                </h3>
                <p className="text-slate-300 mb-8 max-w-xl mx-auto">
                  Start with a Snapshot Report to see exactly where you stand, 
                  then let us help you build the reputation you deserve.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/lookup">
                    <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                      Get Your $99 Report
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    <HeadphonesIcon className="mr-2 h-5 w-5" />
                    Schedule a Demo
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
